import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '@/store'
import { updatePreviewResponse } from '@/store/slices/preview'
import { cn } from '@/lib/utils'
import ImageIcon from '@/assets/builder/icon/ImageIcon'

interface OptionType {
    id: string
    text?: string
    thumbnailUrl?: string
    imageUrl?: string
    [key: string]: any
}

interface SingleSelectImageProps {
    onAutoProceed?: () => void
    isTransitioning?: boolean
}

const SingleSelectImage: React.FC<SingleSelectImageProps> = ({
    onAutoProceed,
    isTransitioning = false,
}) => {
    const dispatch = useDispatch()
    const { selectedQuestionId } = useSelector((state: any) => state.preview)

    const question = useSelector((state: RootState) =>
        (state.preview.questions as any[]).find(
            (q: any) => q.questionId === selectedQuestionId
        )
    )

    const savedResponse = useSelector(
        (state: RootState) => state.preview.previewResponses[selectedQuestionId]
    )

    const options: OptionType[] = question?.config?.options || []

    const [selectedOption, setSelectedOption] = useState<string | null>(
        savedResponse?.optionId || null
    )

    useEffect(() => {
        if (savedResponse) {
            setSelectedOption(savedResponse.optionId || null)
        } else {
            setSelectedOption(null)
        }
    }, [savedResponse])

    useEffect(() => {
        if (selectedOption) {
            dispatch(
                updatePreviewResponse({
                    questionId: selectedQuestionId,
                    response: {
                        optionId: selectedOption,
                    },
                })
            )
        }
    }, [selectedOption, dispatch, selectedQuestionId])

    if (!question) return null

    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 gap-4 mt-6">
            {options.map((option: OptionType, idx: number) => (
                <div className="flex items-start gap-2" key={option.id}>
                    <button
                        type="button"
                        tabIndex={0}
                        aria-pressed={selectedOption === option.id}
                        className={cn(
                            'relative flex flex-col rounded-xl p-3 cursor-pointer transition-all focus:outline-none w-full min-w-[160px] max-w-[240px] min-h-[220px] border transition-colors',
                            selectedOption === option.id
                                ? 'bg-gray-100 border-gray-200'
                                : 'bg-white border-gray-200'
                        )}
                        onClick={() => {
                            // Prevent selection during transition
                            if (isTransitioning) return

                            setSelectedOption(option.id)

                            // Auto-proceed after brief delay for visual feedback
                            if (onAutoProceed) {
                                setTimeout(() => {
                                    onAutoProceed()
                                }, 300)
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                // Prevent selection during transition
                                if (isTransitioning) return

                                setSelectedOption(option.id)

                                // Auto-proceed after brief delay for visual feedback
                                if (onAutoProceed) {
                                    setTimeout(() => {
                                        onAutoProceed()
                                    }, 300)
                                }
                            }
                        }}
                    >
                        {!question.config.hideLabel && (
                            <span
                                className="mb-2 font-medium block w-full text-xs text-gray-700 text-left"
                                title={option.text || `Response ${idx + 1}`}
                            >
                                {option.text || `Response ${idx + 1}`}
                            </span>
                        )}
                        <div
                            className={cn(
                                'flex items-center justify-center rounded-md overflow-hidden aspect-square w-full bg-transparent',
                                !option.imageUrl && 'bg-gray-100'
                            )}
                        >
                            {option.imageUrl ? (
                                <img
                                    src={option.imageUrl}
                                    alt={option.text || `Response ${idx + 1}`}
                                    className="object-contain w-full h-full"
                                />
                            ) : (
                                <ImageIcon className="text-gray-400 w-12 h-12" />
                            )}
                        </div>
                    </button>
                </div>
            ))}
        </div>
    )
}

export default SingleSelectImage
